/**
 * Vista: Catálogo de Líneas Comerciales
 * Sigue el patrón de AlmacenesList: header, stats, filtros, tabla, modal.
 */

import lineaComercialService from '../../services/lineaComercial.service.js';
import permisos from '../../services/permissions.service.js';
import modal from '../../utils/modal.js';
import toast from '../../utils/toast.js';

const esc = (s) => String(s ?? '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');

export default {
  data: {
    lineas: [],
    loading: true,
    editing: null,
    filters: {
      search: '',
      activo: ''
    },
    stats: { total: 0, activas: 0, inactivas: 0 }
  },

  async render() {
    return `
      <div class="page-header">
        <h1>Catálogo de Líneas Comerciales</h1>
        ${permisos.canCreate('lineas_comerciales') ? `
          <button class="btn btn-primary" id="btnNuevaLinea">
            <span class="icon">➕</span> Nueva línea
          </button>
        ` : ''}
      </div>

      <!-- Estadísticas -->
      <div class="stats-grid" id="statsContainer">
        <div class="loading-spinner"></div>
      </div>

      <!-- Filtros -->
      <div class="filters-card">
        <div class="filters-grid">
          <div class="form-group">
            <label>Buscar</label>
            <input type="text" id="searchInput" placeholder="Código o descripción..." class="form-control" />
          </div>
          <div class="form-group">
            <label>Estado</label>
            <select id="activoFilter" class="form-control">
              <option value="">Todos</option>
              <option value="true" selected>Activas</option>
              <option value="false">Inactivas</option>
            </select>
          </div>
          <div class="form-group" style="display:flex;align-items:flex-end;">
            <button class="btn btn-secondary" id="btnLimpiarFiltros">Limpiar</button>
          </div>
        </div>
      </div>

      <!-- Tabla -->
      <div class="card">
        <div class="table-container">
          <table class="table">
            <thead>
              <tr>
                <th>Código</th>
                <th>Descripción</th>
                <th>Estado</th>
                <th class="tbl-hide-mobile" style="text-align:right;">Acciones</th>
              </tr>
            </thead>
            <tbody id="lineasTableBody">
              <tr>
                <td colspan="4" class="text-center">
                  <div class="loading-spinner"></div>
                  <p>Cargando líneas...</p>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <!-- Modal crear/editar -->
      <div id="lineaModal" class="modal" style="display:none;">
        <div class="modal-content" style="max-width:500px;">
          <div class="modal-header">
            <h2 id="modalTitle">Nueva línea</h2>
            <button class="modal-close" id="btnCerrarModal">&times;</button>
          </div>
          <form id="lineaForm">
            <div class="modal-body">
              <div class="form-group">
                <label>Código *</label>
                <input type="text" id="lc_codigo" class="form-control" maxlength="10" required
                       placeholder="Ej: TRACT" style="text-transform:uppercase;" />
                <small class="text-muted">Hasta 10 caracteres, letras mayúsculas y números.</small>
              </div>
              <div class="form-group">
                <label>Descripción *</label>
                <input type="text" id="lc_descripcion" class="form-control" maxlength="100" required
                       placeholder="Ej: TRACTORES Y COSECHADORAS" />
              </div>
            </div>
            <div class="modal-footer">
              <button type="button" class="btn btn-secondary" id="btnCancelar">Cancelar</button>
              <button type="submit" class="btn btn-primary">Guardar</button>
            </div>
          </form>
        </div>
      </div>
    `;
  },

  async afterRender() {
    if (!permisos.canView('lineas_comerciales')) {
      document.getElementById('content').innerHTML = '<div style="padding:2rem;text-align:center;"><h2>Acceso denegado</h2></div>';
      return;
    }
    window.currentView = this;
    this.setupListeners();
    await this.cargar();
  },

  setupListeners() {
    document.getElementById('btnNuevaLinea')?.addEventListener('click', () => this.abrirModal());
    document.getElementById('btnLimpiarFiltros')?.addEventListener('click', () => this.limpiarFiltros());
    document.getElementById('btnCerrarModal')?.addEventListener('click', () => this.cerrarModal());
    document.getElementById('btnCancelar')?.addEventListener('click', () => this.cerrarModal());
    document.getElementById('lineaForm')?.addEventListener('submit', (e) => {
      e.preventDefault();
      this.guardar();
    });
    let searchTimeout;
    document.getElementById('searchInput')?.addEventListener('input', (e) => {
      clearTimeout(searchTimeout);
      searchTimeout = setTimeout(() => {
        this.data.filters.search = e.target.value;
        this.cargar();
      }, 300);
    });
    document.getElementById('activoFilter')?.addEventListener('change', (e) => {
      this.data.filters.activo = e.target.value;
      this.cargar();
    });
  },

  limpiarFiltros() {
    this.data.filters = { search: '', activo: '' };
    document.getElementById('searchInput').value = '';
    document.getElementById('activoFilter').value = '';
    this.cargar();
  },

  async cargar() {
    const tbody = document.getElementById('lineasTableBody');
    try {
      const params = {};
      if (this.data.filters.search) params.search = this.data.filters.search;
      if (this.data.filters.activo === 'true') params.activo = true;
      else if (this.data.filters.activo === 'false') params.activo = false;
      else params.activo = 'all';

      const res = await lineaComercialService.listar(params);
      this.data.lineas = res?.data?.lineas || res?.lineas || [];
      await this.calcularStats();
      this.renderEstadisticas();
      this.renderTabla();
    } catch (err) {
      tbody.innerHTML = `<tr><td colspan="5" class="text-center" style="color:var(--danger);padding:2rem;">${esc(err.message || 'Error al cargar')}</td></tr>`;
      toast.error(err.message || 'Error al cargar líneas');
    }
  },

  // Stats locales (las del filtro actual)
  async calcularStats() {
    // Pedir totales sin filtrar por activo para el contador completo
    try {
      const res = await lineaComercialService.listar({ activo: 'all' });
      const todas = res?.data?.lineas || res?.lineas || [];
      this.data.stats = {
        total: todas.length,
        activas: todas.filter(l => l.activo).length,
        inactivas: todas.filter(l => !l.activo).length
      };
    } catch {
      // Fallback: usar lo que tenemos cargado
      this.data.stats = {
        total: this.data.lineas.length,
        activas: this.data.lineas.filter(l => l.activo).length,
        inactivas: this.data.lineas.filter(l => !l.activo).length
      };
    }
  },

  renderEstadisticas() {
    const container = document.getElementById('statsContainer');
    if (!container) return;
    const s = this.data.stats;
    container.innerHTML = `
      <div class="stat-card">
        <div class="stat-icon">🏷️</div>
        <div class="stat-content">
          <div class="stat-value">${s.total || 0}</div>
          <div class="stat-label">Total Líneas</div>
        </div>
      </div>
      <div class="stat-card stat-success">
        <div class="stat-icon">✅</div>
        <div class="stat-content">
          <div class="stat-value">${s.activas || 0}</div>
          <div class="stat-label">Activas</div>
        </div>
      </div>
      <div class="stat-card stat-warning">
        <div class="stat-icon">⏸️</div>
        <div class="stat-content">
          <div class="stat-value">${s.inactivas || 0}</div>
          <div class="stat-label">Inactivas</div>
        </div>
      </div>
    `;
  },

  renderTabla() {
    const tbody = document.getElementById('lineasTableBody');
    if (!tbody) return;
    if (this.data.lineas.length === 0) {
      tbody.innerHTML = '<tr><td colspan="4" class="text-center" style="padding:2rem;color:var(--gray-500);">Sin registros.</td></tr>';
      return;
    }
    tbody.innerHTML = this.data.lineas.map(l => `
      <tr ${!l.activo ? 'style="opacity:0.55;"' : ''}>
        <td><code>${esc(l.codigo)}</code></td>
        <td>${esc(l.descripcion)}</td>
        <td>
          <span style="padding:0.15rem 0.5rem;border-radius:4px;font-size:0.75rem;font-weight:600;
            background:${l.activo ? '#d1fae5' : '#fee2e2'};color:${l.activo ? '#065f46' : '#991b1b'};">
            ${l.activo ? 'Activa' : 'Inactiva'}
          </span>
        </td>
        <td style="text-align:right;">
          ${l.activo && permisos.canEdit('lineas_comerciales') ? `
            <button class="btn-icon btn-secondary" title="Editar" onclick="window.currentView.abrirModal('${esc(l.codigo)}')">✏️</button>
          ` : ''}
          ${permisos.can('lineas_comerciales', 'cambiar_estado') ? `
            <button class="btn-icon ${l.activo ? 'btn-warning' : 'btn-success'}"
                    title="${l.activo ? 'Desactivar' : 'Activar'}"
                    onclick="window.currentView.${l.activo ? 'desactivar' : 'reactivar'}('${esc(l.codigo)}')">
              ${l.activo ? '⏸️' : '▶️'}
            </button>
          ` : ''}
          ${permisos.canDelete('lineas_comerciales') ? `
            <button class="btn-icon btn-danger" title="Eliminar" onclick="window.currentView.eliminar('${esc(l.codigo)}')">🗑️</button>
          ` : ''}
        </td>
      </tr>
    `).join('');
  },

  abrirModal(codigo = null) {
    const linea = codigo ? this.data.lineas.find(l => l.codigo === codigo) : null;
    this.data.editing = linea;
    document.getElementById('modalTitle').textContent = linea ? 'Editar línea' : 'Nueva línea';
    document.getElementById('lc_codigo').value = linea?.codigo || '';
    document.getElementById('lc_codigo').readOnly = !!linea;
    document.getElementById('lc_descripcion').value = linea?.descripcion || '';
    document.getElementById('lineaModal').style.display = 'flex';
  },

  cerrarModal() {
    document.getElementById('lineaModal').style.display = 'none';
    this.data.editing = null;
  },

  async guardar() {
    const codigo = document.getElementById('lc_codigo').value.trim().toUpperCase();
    const descripcion = document.getElementById('lc_descripcion').value.trim();

    if (!codigo || !descripcion) {
      toast.warning('Código y descripción son obligatorios');
      return;
    }
    if (!/^[A-Z0-9]+$/.test(codigo)) {
      toast.warning('El código solo puede tener letras mayúsculas y números');
      return;
    }

    try {
      if (this.data.editing) {
        await lineaComercialService.actualizar(this.data.editing.codigo, { descripcion });
        toast.success('Línea actualizada');
      } else {
        // El orden lo asigna el backend con default 0; admins lo pueden ajustar
        // vía SQL o futura UI. No se expone en el form para simplificar UX.
        await lineaComercialService.crear({ codigo, descripcion });
        toast.success('Línea creada');
      }
      this.cerrarModal();
      await this.cargar();
    } catch (err) {
      toast.error(err.message || 'Error al guardar');
    }
  },

  async desactivar(codigo) {
    const linea = this.data.lineas.find(l => l.codigo === codigo);
    const titulo = linea ? `${esc(linea.codigo)} — ${esc(linea.descripcion)}` : esc(codigo);
    modal.confirm(
      'Desactivar línea',
      `<p>¿Estás seguro de desactivar <strong>${titulo}</strong>?</p>`,
      async () => {
        try {
          await lineaComercialService.desactivar(codigo);
          toast.success('Línea desactivada');
          await this.cargar();
        } catch (err) {
          toast.error(err.message || 'Error al desactivar');
        }
      }
    );
  },

  async reactivar(codigo) {
    const linea = this.data.lineas.find(l => l.codigo === codigo);
    const titulo = linea ? `${esc(linea.codigo)} — ${esc(linea.descripcion)}` : esc(codigo);
    modal.confirm(
      'Reactivar línea',
      `<p>¿Estás seguro de reactivar <strong>${titulo}</strong>?</p>`,
      async () => {
        try {
          await lineaComercialService.reactivar(codigo);
          toast.success('Línea reactivada');
          await this.cargar();
        } catch (err) {
          toast.error(err.message || 'Error al reactivar');
        }
      }
    );
  },

  async eliminar(codigo) {
    const linea = this.data.lineas.find(l => l.codigo === codigo);
    const titulo = linea ? `${esc(linea.codigo)} — ${esc(linea.descripcion)}` : esc(codigo);
    modal.confirm(
      'Confirmar eliminación',
      `<p>¿Estás seguro de eliminar <strong>${titulo}</strong>? Esta acción no se puede deshacer.</p>`,
      async () => {
        try {
          await lineaComercialService.eliminar(codigo);
          toast.success('Línea eliminada');
          await this.cargar();
        } catch (err) {
          toast.error(err.message || 'Error al eliminar');
        }
      }
    );
  }
};
